import { useState, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/components/ui/input'

export interface SearchSuggestResults<T> {
  results: T[]
  isLoading: boolean
}

/** Optional trailing option shown after the results (e.g. "New SKU"). */
export interface SearchSuggestExtraOption {
  show: boolean
  copyText: string
  render: (highlighted: boolean) => ReactNode
  onSelect: () => void
}

interface SearchSuggestCellProps<T> {
  value: string | null
  isEditing: boolean
  onCancelEdit: () => void
  onTab?: (shiftKey: boolean) => boolean | void
  onEnter?: (shiftKey: boolean) => void
  placeholder: string
  /** Hook-like data source; called on every render with the live query. */
  useResults: (query: string, enabled: boolean) => SearchSuggestResults<T>
  getKey: (item: T) => string
  getCopyText: (item: T) => string
  renderItem: (item: T) => ReactNode
  onSelect: (item: T) => void
  onClear: () => void
  /** Builds the trailing option from the current query and results. */
  extraOption?: (query: string, results: T[]) => SearchSuggestExtraOption
  /** Read-only rendering when the cell is not being edited. */
  display: ReactNode
}

/**
 * Typeahead grid cell: a text input with a portaled suggestion dropdown,
 * keyboard navigation (↑/↓, Enter/Tab select, Esc cancel) and a "Clear value"
 * option when the input is emptied. Shared by the SKU and vendor pickers.
 */
export function SearchSuggestCell<T>({
  value,
  isEditing,
  onCancelEdit,
  onTab,
  onEnter,
  placeholder,
  useResults,
  getKey,
  getCopyText,
  renderItem,
  onSelect,
  onClear,
  extraOption,
  display,
}: SearchSuggestCellProps<T>) {
  const [searchQuery, setSearchQuery] = useState(value || '')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [showDropdown, setShowDropdown] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(blurTimeoutRef.current), [isEditing])

  const { results, isLoading } = useResults(
    searchQuery,
    isEditing && searchQuery.length >= 1
  )

  const extra = extraOption?.(searchQuery, results)
  const showExtraOption = !!extra?.show

  // Total items including the extra option
  const totalItems = results.length + (showExtraOption ? 1 : 0)

  // Reset state when editing starts/stops
  useEffect(() => {
    if (isEditing) {
      setSearchQuery(value || '')
      setHighlightedIndex(-1)
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
    }
  }, [isEditing, value])

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Show dropdown when we have results or an extra option
  useEffect(() => {
    if (
      isEditing &&
      (results.length > 0 ||
        showExtraOption ||
        (searchQuery.length === 0 && value))
    ) {
      setShowDropdown(true)
    }
  }, [isEditing, results.length, showExtraOption, searchQuery, value])

  // Calculate dropdown position and update on scroll
  useEffect(() => {
    if (!showDropdown || !containerRef.current) return

    const updatePosition = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()

      // Check if we should show dropdown above or below
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight =
        dropdownRef.current?.getBoundingClientRect().height || 240
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

      setDropdownPosition({
        top: showAbove ? rect.top - dropdownHeight : rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 300),
      })
    }

    // Use rAF to ensure DOM is settled before initial measurement
    const rafId = requestAnimationFrame(updatePosition)

    // Update position on scroll (any scrollable parent)
    const handleScroll = () => updatePosition()
    const handleResize = () => updatePosition()

    // Listen to scroll events on window and any parent with overflow
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [showDropdown])

  const handleSelect = (item: T) => {
    onSelect(item)
    setShowDropdown(false)
  }

  const handleSelectExtra = () => {
    extra?.onSelect()
    setShowDropdown(false)
  }

  const handleClear = () => {
    onClear()
    setShowDropdown(false)
  }

  // Select whatever is highlighted; returns false when nothing was selected.
  const selectHighlighted = (): boolean => {
    if (highlightedIndex < 0) return false
    if (highlightedIndex < results.length) {
      handleSelect(results[highlightedIndex])
    } else if (showExtraOption) {
      handleSelectExtra()
    }
    return true
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.nativeEvent.isComposing) return

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault()

      // Copy the currently highlighted dropdown option instead of the cell value.
      if (highlightedIndex >= 0) {
        if (highlightedIndex < results.length) {
          navigator.clipboard
            .writeText(getCopyText(results[highlightedIndex]))
            .catch(() => {})
        } else if (extra && showExtraOption) {
          navigator.clipboard.writeText(extra.copyText).catch(() => {})
        }
      }

      return
    }

    if (!showDropdown || totalItems === 0) {
      if (e.key === 'Escape') {
        e.preventDefault()
        setSearchQuery(value || '')
        onCancelEdit()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (searchQuery.length === 0 && value) {
          handleClear()
        } else if (searchQuery !== (value || '')) {
          // Keep an unresolved lookup open; Enter must not discard typed input.
          return
        } else {
          onCancelEdit()
        }
        onEnter?.(e.shiftKey)
      } else if (e.key === 'Tab') {
        onCancelEdit()
        if (onTab?.(e.shiftKey) !== false) e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => (prev + 1) % totalItems)
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems)
        break
      case 'Enter':
        e.preventDefault()
        if (selectHighlighted()) {
          // selected
        } else if (searchQuery === (value || '')) {
          onCancelEdit()
        } else if (showExtraOption && !isLoading) {
          // Nothing highlighted but there's input: take the extra option
          handleSelectExtra()
        } else {
          return
        }
        onEnter?.(e.shiftKey)
        break
      case 'Tab':
        if (!selectHighlighted()) onCancelEdit()
        if (onTab?.(e.shiftKey) !== false) e.preventDefault()
        break
      case 'Escape':
        e.preventDefault()
        setSearchQuery(value || '')
        onCancelEdit()
        break
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the related target is within the dropdown
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return
    }
    // Delay to allow click on dropdown items
    clearTimeout(blurTimeoutRef.current)
    blurTimeoutRef.current = setTimeout(() => {
      if (
        !dropdownRef.current?.contains(document.activeElement) &&
        !containerRef.current?.contains(document.activeElement)
      ) {
        setShowDropdown(false)
        onCancelEdit()
      }
    }, 150)
  }

  if (!isEditing) return <>{display}</>

  return (
    <div className="relative" ref={containerRef}>
      <Input
        ref={inputRef}
        value={searchQuery}
        onChange={e => {
          setSearchQuery(e.target.value)
          setHighlightedIndex(-1)
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
        placeholder={placeholder}
      />

      {showDropdown &&
        (searchQuery.length >= 1 || value) &&
        createPortal(
          <div
            ref={dropdownRef}
            data-sku-dropdown
            className="dag-dropdown-portal fixed z-[9999] pointer-events-auto max-h-60 overflow-auto rounded border border-[#D6DAE1] bg-white shadow-md"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: dropdownPosition.width,
            }}
          >
            {searchQuery.length === 0 && value && (
              <div
                className="cursor-pointer px-2 py-1.5 text-sm hover:bg-muted flex items-center gap-2 text-muted-foreground"
                onMouseDown={e => e.preventDefault()}
                onClick={handleClear}
              >
                Clear value
              </div>
            )}
            {searchQuery.length >= 1 && (
              <>
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : (
                  <>
                    {results.map((item, index) => (
                      <div
                        key={getKey(item)}
                        className={`cursor-pointer px-2 py-1.5 text-sm hover:bg-muted flex items-center justify-between gap-2 ${
                          highlightedIndex === index ? 'bg-muted' : ''
                        }`}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => handleSelect(item)}
                      >
                        {renderItem(item)}
                      </div>
                    ))}

                    {extra && showExtraOption && (
                      <div
                        onMouseEnter={() => setHighlightedIndex(results.length)}
                        onMouseDown={e => e.preventDefault()}
                        onClick={handleSelectExtra}
                      >
                        {extra.render(highlightedIndex === results.length)}
                      </div>
                    )}

                    {results.length === 0 && !showExtraOption && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No results found
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
